import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const CreateJobModal = ({ isOpen, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    location: '',
    workType: '',
    requiredSkills: [],
    startDate: '',
    duration: '',
    rateType: 'hourly',
    hourlyRate: '',
    projectRate: '',
    urgency: 'normal'
  });

  const [errors, setErrors] = useState({});

  const workTypeOptions = [
    { value: 'solar', label: 'Zonnepanelen' },
    { value: 'heatpump', label: 'Warmtepompen' },
    { value: 'battery', label: 'Thuisbatterijen' },
    { value: 'maintenance', label: 'Technisch onderhoud' },
    { value: 'ventilation', label: 'Ventilatie systemen' }
  ];

  const certificationOptions = [
    { value: 'solar-installation', label: 'Zonnepaneel Installatie' },
    { value: 'heat-pump', label: 'Warmtepomp Specialist' },
    { value: 'battery-systems', label: 'Thuisbatterij Systemen' },
    { value: 'electrical-licensed', label: 'Elektricien Vergunning' },
    { value: 'hvac-certified', label: 'HVAC Gecertificeerd' }
  ];

  const skillOptions = [
    { value: 'hvac-certified', label: 'HVAC Gecertificeerd' },
    { value: 'electrical-licensed', label: 'Elektricien Vergunning' },
    { value: 'plumbing-certified', label: 'Loodgieter Gecertificeerd' },
    { value: 'solar-installation', label: 'Zonnepaneel Installatie' },
    { value: 'heat-pump', label: 'Warmtepomp Specialist' }
  ];

  const durationOptions = [
    { value: '1-day', label: '1 dag' },
    { value: '2-3-days', label: '2-3 dagen' },
    { value: '1-week', label: '1 week' },
    { value: '2-weeks', label: '2 weken' },
    { value: '1-month', label: '1 maand' },
    { value: 'ongoing', label: 'Doorlopend' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.title?.trim()) {
      newErrors.title = 'Titel is verplicht';
    }
    if (!formData?.description?.trim()) {
      newErrors.description = 'Beschrijving is verplicht';
    }
    if (!formData?.location?.trim()) {
      newErrors.location = 'Locatie is verplicht';
    }
    if (!formData?.workType) {
      newErrors.workType = 'Werktype is verplicht';
    }
    if (formData?.requiredSkills?.length === 0) {
      newErrors.requiredSkills = 'Selecteer minimaal één vaardigheid';
    }
    if (!formData?.startDate) {
      newErrors.startDate = 'Startdatum is verplicht';
    }
    if (!formData?.duration) {
      newErrors.duration = 'Duur is verplicht';
    }
    if (formData?.rateType === 'hourly' && !formData?.hourlyRate) {
      newErrors.hourlyRate = 'Uurtarief is verplicht';
    }
    if (formData?.rateType === 'project' && !formData?.projectRate) {
      newErrors.projectRate = 'Projecttarief is verplicht';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (validateForm()) {
      onSubmit(formData);
      handleClose();
    }
  };

  const handleClose = () => {
    setFormData({
      title: '',
      description: '',
      location: '',
      workType: '',
      requiredSkills: [],
      startDate: '',
      duration: '',
      rateType: 'hourly',
      hourlyRate: '',
      projectRate: '',
      urgency: 'normal'
    });
    setErrors({});
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-1030 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-background/80 backdrop-blur-sm" onClick={handleClose} />
      <div 
        className="relative bg-white border-2 border-slate-200 rounded-lg w-full max-w-3xl max-h-[90vh] overflow-hidden"
        style={{
          boxShadow: '0 20px 60px rgba(0,0,0,0.2), 0 10px 30px rgba(0,0,0,0.12)'
        }}
      >
        <div className="sticky top-0 bg-card border-b border-border px-4 md:px-6 py-4 flex items-center justify-between z-10">
          <h2 className="font-heading font-semibold text-xl md:text-2xl text-foreground">
            Nieuwe Opdracht Plaatsen
          </h2>
          <Button variant="ghost" size="icon" onClick={handleClose}>
            <Icon name="X" size={24} />
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="overflow-y-auto max-h-[calc(90vh-140px)]">
          <div className="px-4 md:px-6 py-6 space-y-6">
            <Input
              label="Opdracht Titel"
              type="text"
              placeholder="Bijv. Zonnepanelen installatie woning"
              value={formData?.title}
              onChange={(e) => handleInputChange('title', e?.target?.value)}
              error={errors?.title}
              required
            />

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Beschrijving <span className="text-error">*</span>
              </label>
              <textarea
                className="w-full min-h-32 px-3 py-2 bg-background border border-input rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-background transition-smooth resize-none"
                placeholder="Geef een gedetailleerde beschrijving van de opdracht..."
                value={formData?.description}
                onChange={(e) => handleInputChange('description', e?.target?.value)}
              />
              {errors?.description && (
                <p className="mt-1 text-xs text-error">{errors?.description}</p>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Locatie"
                type="text"
                placeholder="Stad of postcode"
                value={formData?.location}
                onChange={(e) => handleInputChange('location', e?.target?.value)}
                error={errors?.location}
                required
              />

              <Select
                label="Werktype"
                options={workTypeOptions}
                value={formData?.workType}
                onChange={(value) => handleInputChange('workType', value)}
                error={errors?.workType}
                required
                placeholder="Selecteer werktype"
              />
            </div>

            <Select
              label="Vereiste Vaardigheden"
              options={skillOptions}
              value={formData?.requiredSkills}
              onChange={(value) => handleInputChange('requiredSkills', value)}
              error={errors?.requiredSkills}
              required
              multiple
              searchable
              placeholder="Selecteer vaardigheden"
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Startdatum"
                type="date"
                value={formData?.startDate}
                onChange={(e) => handleInputChange('startDate', e?.target?.value)}
                error={errors?.startDate}
                required
              />

              <Select
                label="Duur"
                options={durationOptions}
                value={formData?.duration}
                onChange={(value) => handleInputChange('duration', value)}
                error={errors?.duration}
                required
                placeholder="Selecteer duur"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-3">
                Tarief Type <span className="text-error">*</span>
              </label>
              <div className="flex gap-4">
                <Checkbox
                  label="Uurtarief"
                  checked={formData?.rateType === 'hourly'}
                  onChange={() => handleInputChange('rateType', 'hourly')}
                />
                <Checkbox
                  label="Projecttarief"
                  checked={formData?.rateType === 'project'}
                  onChange={() => handleInputChange('rateType', 'project')}
                />
              </div>
            </div>

            {formData?.rateType === 'hourly' && (
              <Input
                label="Uurtarief (€)"
                type="number"
                placeholder="Bijv. 45"
                value={formData?.hourlyRate}
                onChange={(e) => handleInputChange('hourlyRate', e?.target?.value)}
                error={errors?.hourlyRate}
                required
              />
            )}

            {formData?.rateType === 'project' && (
              <Input
                label="Projecttarief (€)"
                type="number"
                placeholder="Bijv. 1200"
                value={formData?.projectRate}
                onChange={(e) => handleInputChange('projectRate', e?.target?.value)}
                error={errors?.projectRate}
                required
              />
            )}

            <div>
              <label className="block text-sm font-medium text-foreground mb-3">
                Urgentie
              </label>
              <div className="flex gap-4">
                <Checkbox
                  label="Normaal"
                  checked={formData?.urgency === 'normal'}
                  onChange={() => handleInputChange('urgency', 'normal')}
                />
                <Checkbox
                  label="Hoog"
                  checked={formData?.urgency === 'high'}
                  onChange={() => handleInputChange('urgency', 'high')}
                />
              </div>
            </div>
          </div>

          <div className="sticky bottom-0 bg-card border-t border-border px-4 md:px-6 py-4 flex items-center justify-end gap-3">
            <Button variant="outline" onClick={handleClose} type="button">
              Annuleren
            </Button>
            <Button variant="default" type="submit" iconName="Plus" iconPosition="left">
              Opdracht Plaatsen
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateJobModal;